import React, { useEffect, useState } from "react";
import LinearLayout from "../layout/LinearLayout";
import TextView from "../textview/TextView";
import ButtonSimp from "../button";
import axios from "../../utils/axiosInstance";
import { FaPlus, FaMinus, FaTrash } from "react-icons/fa";
import BootImage from "../Image/bootImg/bootImg";
import BasicInput from "../edittext/basicInput/basicInput";
import { ChevronDown, ChevronUp } from "lucide-react";

function formatPrice(n, suffix = "$") {
  const num = Number(n || 0);
  return `${num.toLocaleString()}${suffix}`;
}

const CartTable = ({
  items = [],
  onUpdateQty,
  onRemove,
  onCheckout,
  currency = "$",
}) => {
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const totalQty = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
  const totalPrice = items.reduce(
    (s, it) => s + Number(it.price || 0) * Number(it.quantity || 0),
    0
  );

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const productData = {};
        for (let it of items) {
          const res = await axios.get(`/products/${it.productId}`);
          productData[it.productId] = res.data.data;
        }
        setProducts(productData);
      } catch (err) {
        console.error("❌ Error loading data:", err);
        setError("Error loading data!");
      } finally {
        setLoading(false);
      }
    };
    if (items.length > 0) fetchProducts();
  }, [items]);

  if (!items.length)
    return (
      <LinearLayout orientation="vertical" gap={2} padding={3}>
        <TextView size="lg" bold>
          🛒 سبد خرید خالی است
        </TextView>
        <TextView size="sm" color="muted">
          محصولی به سبد اضافه نشده است.
        </TextView>
      </LinearLayout>
    );

  if (loading) return <p className="m-3">⏳ در حال بارگذاری...</p>;
  if (error) return <p className="text-danger">{error}</p>;

  const toggleExpand = (id) => setExpanded(expanded === id ? null : id);

  return (
    <LinearLayout orientation="vertical" gap={3} padding={2}>
      {/* ✅ نمایش دسکتاپ */}
      <div className="d-none d-lg-block">
        <table className="table table-striped align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "45%" }}>Product</th>
              <th className="text-center">Price</th>
              <th className="text-center">Quantity</th>
              <th className="text-center">Total</th>
              <th className="text-center"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const product = products[it.productId] || {};
              const sub = Number(it.price || 0) * Number(it.quantity || 0);
              return (
                <tr key={it.productId}>
                  <td>
                    <div className="d-flex align-items-center gap-3">
                      <img
                        src={
                          product?.images?.[0]
                            ? product.images[0]
                            : "/placeholder.png"
                        }
                        alt={product.title || "بدون عنوان"}
                        width={120}
                        height={80}
                        style={{ objectFit: "cover", borderRadius: 10 }}
                      />
                      <div>
                        <div className="fw-bold">{product.title}</div>
                        <small className="text-muted">
                          Product #{String(it.productId).slice(-6)}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td className="text-center">
                    {formatPrice(it.price, currency)}
                  </td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <ButtonSimp
                        color="danger"
                        padding="1"
                        text={<FaMinus className="mb-1" />}
                        clicked={() =>
                          onUpdateQty?.(
                            it.productId,
                            Math.max(1, Number(it.quantity) - 1)
                          )
                        }
                      />
                      <BasicInput
                        type="number"
                        value={Number(it.quantity || 1)}
                        onChange={(e) =>
                          onUpdateQty?.(
                            it.productId,
                            Math.max(1, parseInt(e.target.value || "1", 10))
                          )
                        }
                        width="70px"
                        textAlign="center"
                      />
                      <ButtonSimp
                        color="success"
                         padding="1"
                        text={<FaPlus  className="mb-1" />}
                        clicked={() =>
                          onUpdateQty?.(it.productId, Number(it.quantity) + 1)
                        }
                      />
                    </div>
                  </td>
                  <td className="text-center">
                    {formatPrice(sub, currency)}
                  </td>
                  <td className="text-center">
                    <ButtonSimp
                      color="danger"
                       padding="1"
                      text={<FaTrash className="mb-1"/>}
                      clicked={() => onRemove?.(it.productId)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ✅ نمایش موبایل (کارت تاشو شبیه کمپین) */}
      <div className="d-lg-none">
        {items.map((it) => {
          const product = products[it.productId] || {};
          const sub = Number(it.price || 0) * Number(it.quantity || 0);

          return (
            <div
              key={it.productId}
              className="border rounded shadow-sm mb-3 overflow-hidden"
            >
              <div
                className="d-flex justify-content-between align-items-center p-2 bg-light"
                onClick={() => toggleExpand(it.productId)}
                role="button"
              >
                <strong>{product.title || "بدون عنوان"}</strong>
                {expanded === it.productId ? <ChevronUp /> : <ChevronDown />}
              </div>

              {expanded === it.productId && (
                <div className="p-2 bg-white small">
                  <img
                    src={
                      product?.images?.[0]
                        ? product.images[0]
                        : "/placeholder.png"
                    }
                    alt={product.title}
                    className="img-thumbnail mb-2"
                  />

                  <div className="mb-1">
                    <strong>Price: </strong>
                    {formatPrice(it.price, currency)}
                  </div>
                  <div className="mb-1">
                    <strong>Total: </strong>
                    {formatPrice(sub, currency)}
                  </div>
                  <div className="d-flex justify-content-center align-items-center gap-2 my-2">
                    <ButtonSimp
                      color="danger"
                      padding="1"
                      text={<FaMinus className="mb-1"/>}
                      clicked={() =>
                        onUpdateQty?.(
                          it.productId,
                          Math.max(1, Number(it.quantity) - 1)
                        )
                      }
                    />
                    <BasicInput
                      type="number"
                      value={Number(it.quantity || 1)}
                      onChange={(e) =>
                        onUpdateQty?.(
                          it.productId,
                          Math.max(1, parseInt(e.target.value || "1", 10))
                        )
                      }
                      width="70px"
                      textAlign="center"
                    />
                    <ButtonSimp
                      color="success"
                       padding="1"
                      text={<FaPlus className="mb-1" />}
                      clicked={() =>
                        onUpdateQty?.(it.productId, Number(it.quantity) + 1)
                      }
                    />
                  </div>
                  <div className="text-center">
                    <ButtonSimp
                     padding="1"
                      color="danger"
                      text={<FaTrash className="mb-1"/>}
                      clicked={() => onRemove?.(it.productId)}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

     
    </LinearLayout>
  );
};

export default CartTable;


